/**
 * TicketQuickCreateDialog - Minimal dialog for quick ticket creation
 *
 * Following the Quick Create Dialog pattern:
 * - 3-5 fields maximum (Subject, Contact, Priority, Description)
 * - Redirects to detail page after creation for full editing
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { cn } from '../../../lib/utils';
import { isRichTextEmpty } from '../../../shared/utils/htmlToText';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { DialogForm, CompactRichTextEditor } from '../../../shared/components/common';
import {
  CustomFieldsSection,
  activeCustomFieldDefinitions,
  useCustomFieldDefinitions,
  validateCustomFields,
  type CustomFieldErrors,
} from '../../../shared/components/customFields';
import { useCreateTicket } from '../hooks/useTickets';
import { useClipboardPaste, PendingAttachmentsField } from '@/shared/files';
import { ticketApi } from '../services/supportApi';
import { OrganizationContactComboSelect } from '../../master_data/contacts/components/OrganizationContactComboSelect';
import { ProductReferencePicker } from '../../master_data/products/components/ProductReferencePicker';
import { productReferenceFromFields } from '../../master_data/products/types';
import type { Ticket, TicketCreate, TicketPriority } from '../types';
import { toast } from 'sonner';
import { useApiError } from '../../../shared/hooks/useApiError';
import { getFieldErrors } from '../../../shared/utils/errors';

/** How the backend names a custom-field rejection (`field="custom_fields.<key>"`). */
const CUSTOM_FIELD_ERROR_PREFIX = 'custom_fields.';

interface TicketQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill contact from context */
  defaultContactId?: string;
  /** Pre-fill company from context */
  defaultCompanyId?: string;
  /** Pre-fill product from context */
  defaultProductId?: string;
  /** Callback when ticket is created (before navigation) */
  onCreated?: (ticket: Ticket) => void;
  /** If true, don't navigate to detail page after creation */
  skipNavigation?: boolean;
}

interface FormData {
  subject: string;
  description: string;
  contactId: string;
  organizationId: string;
  productId: string;
  priority: TicketPriority;
}

export function TicketQuickCreateDialog({
  open,
  onClose,
  defaultContactId,
  defaultCompanyId,
  defaultProductId,
  onCreated,
  skipNavigation = false,
}: TicketQuickCreateDialogProps) {
  const { t } = useTranslation('support');
  const { t: tcf } = useTranslation('custom_fields');
  const navigate = useNavigate();

  const createMutation = useCreateTicket();
  const { handleMutationError } = useApiError();

  // Attachments: a ticket is most often reported WITH a screenshot, so the
  // gesture has to work here and not only after the ticket exists. Files are
  // held pending and uploaded once the ticket has an id.
  const dialogBodyRef = useRef<HTMLDivElement>(null);
  const {
    pendingFiles,
    handleFileInputChange,
    removePendingFile,
    clearPendingFiles,
    handlePaste,
    handleDrop,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    isDragging,
  } = useClipboardPaste({ maxSizeMB: 25 });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    setError,
  } = useForm<FormData>({
    mode: 'onBlur',
    defaultValues: {
      subject: '',
      description: '',
      contactId: defaultContactId || '',
      organizationId: defaultCompanyId || '',
      productId: defaultProductId || '',
      priority: 'medium',
    },
  });

  // Ticket custom fields, REQUIRED ones only. The create endpoint validates
  // `partial=False`, so a tenant's required `support_ticket` field is not something
  // the detail page can collect afterwards — without it the create is refused, and
  // this dialog is the only create surface some entry points have. The optional ones
  // stay off the dialog: that is what keeps the quick-create budget honest
  // (`.claude/rules/rules-ui-dialogs.md`).
  const [customFields, setCustomFields] = useState<Record<string, unknown>>({});
  const [customFieldErrors, setCustomFieldErrors] = useState<CustomFieldErrors>({});
  const { data: cfData } = useCustomFieldDefinitions('support_ticket');
  const requiredCfDefinitions = useMemo(
    () => activeCustomFieldDefinitions(cfData?.definitions).filter((d) => d.required),
    [cfData?.definitions]
  );

  const contactId = watch('contactId');
  const organizationId = watch('organizationId');
  const productId = watch('productId');

  useEffect(() => {
    if (open) {
      reset({
        subject: '',
        description: '',
        contactId: defaultContactId || '',
        organizationId: defaultCompanyId || '',
        productId: defaultProductId || '',
        priority: 'medium',
      });
      clearPendingFiles();
      setCustomFields({});
      setCustomFieldErrors({});
    }
  }, [open, defaultContactId, defaultCompanyId, defaultProductId, reset, clearPendingFiles]);

  // `handleMutationError` files every field error the envelope names; this drops the
  // custom-field ones on the floor so they never reach react-hook-form (they are
  // rendered by `CustomFieldsSection` instead — see the catch block below).
  const setNonCustomFieldError: typeof setError = (name, error, options) => {
    if (String(name).startsWith(CUSTOM_FIELD_ERROR_PREFIX)) return;
    setError(name, error, options);
  };

  const onSubmit = async (data: FormData) => {
    const cfErrors = validateCustomFields(requiredCfDefinitions, customFields, tcf);
    setCustomFieldErrors(cfErrors);
    if (Object.keys(cfErrors).length > 0) return;

    try {
      const payload: TicketCreate = {
        subject: data.subject,
        description: isRichTextEmpty(data.description) ? undefined : data.description,
        contact_id: data.contactId,
        organization_id: data.organizationId || undefined,
        product_id: data.productId || undefined,
        priority: data.priority,
        channel: 'phone', // Default channel for quick create
        // A tenant with no required field sends exactly the payload it did before.
        ...(requiredCfDefinitions.length > 0 ? { custom_fields: customFields } : {}),
      };
      const newTicket = await createMutation.mutateAsync(payload);

      // Upload before navigating: leaving the page first would abandon them.
      // The ticket already EXISTS by now, so a failed attachment must not be
      // reported as a failed create — that left the dialog open on a form whose
      // ticket had been created, and re-submitting filed a duplicate. Report the
      // ticket as made, say the attachments did not land, and carry on to the
      // ticket where they can be re-attached.
      if (pendingFiles.length > 0) {
        try {
          await Promise.all(
            pendingFiles.map((file) => ticketApi.uploadAttachment(newTicket.id, file))
          );
        } catch {
          toast.error(t('tickets.attachmentsFailed'));
        }
      }

      toast.success(t('tickets.created'));
      onCreated?.(newTicket);
      onClose();

      if (!skipNavigation) {
        navigate(`/support/tickets/${newTicket.id}`);
      }
    } catch (error) {
      // A custom-field rejection names `custom_fields.<key>`, which is no field of
      // this form. Route those onto the inputs that own them — and keep them OUT of
      // react-hook-form: an error set on a name the form never registered is never
      // cleared by validating the fields it did, so `handleSubmit` refuses every
      // later submit. The user then fixes the field and Create does nothing, for the
      // rest of the dialog's life.
      const cfErrors: CustomFieldErrors = {};
      for (const [field, message] of Object.entries(getFieldErrors(error) ?? {})) {
        if (field.startsWith(CUSTOM_FIELD_ERROR_PREFIX)) {
          cfErrors[field.slice(CUSTOM_FIELD_ERROR_PREFIX.length)] = message;
        }
      }
      setCustomFieldErrors(cfErrors);
      handleMutationError(error, setNonCustomFieldError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('tickets.form.title')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      isLoading={createMutation.isPending}
      maxWidth="sm"
    >
      <div
        ref={dialogBodyRef}
        className="space-y-4"
        onPaste={handlePaste}
        onDrop={handleDrop}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
      >
        <Controller
          name="subject"
          control={control}
          rules={{ required: t('tickets.errors.subjectRequired') }}
          render={({ field }) => (
            <div className="space-y-2">
              <Label htmlFor="subject" required>{t('tickets.subject')}</Label>
              <Input
                id="subject"
                {...field}
                placeholder={t('tickets.form.subjectPlaceholder')}
                className={cn(errors.subject && 'border-destructive')}
              />
              {errors.subject && (
                <p className="text-sm text-destructive">{errors.subject.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="contactId"
          control={control}
          rules={{ required: t('tickets.errors.contactRequired') }}
          render={() => (
            <div className="space-y-2">
              <OrganizationContactComboSelect
                organizationId={organizationId}
                contactId={contactId}
                onCompanyChange={(val) => setValue('organizationId', val)}
                onContactChange={(val) => setValue('contactId', val)}
                contactRequired
              />
              {errors.contactId && (
                <p className="text-sm text-destructive">{errors.contactId.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="priority"
          control={control}
          render={({ field }) => (
            <div className="space-y-2">
              <Label htmlFor="priority">{t('tickets.priority')}</Label>
              <Select value={field.value} onValueChange={(v) => field.onChange(v as TicketPriority)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">{t('tickets.priorities.critical')}</SelectItem>
                  <SelectItem value="high">{t('tickets.priorities.high')}</SelectItem>
                  <SelectItem value="medium">{t('tickets.priorities.medium')}</SelectItem>
                  <SelectItem value="low">{t('tickets.priorities.low')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        />

        <Controller
          name="productId"
          control={control}
          render={() => (
            <ProductReferencePicker
              value={productReferenceFromFields(productId)}
              onChange={(ref) => setValue('productId', ref?.product_id ?? '')}
              productOnly
              productLabel={t('tickets.product')}
              productStatusFilter={['active']}
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <div className="space-y-2">
              <Label>{t('tickets.description')}</Label>
              <CompactRichTextEditor
                value={field.value}
                onChange={field.onChange}
                placeholder={t('tickets.form.descriptionPlaceholder')}
                minHeight={80}
              />
            </div>
          )}
        />

        <PendingAttachmentsField
          files={pendingFiles}
          onFileInputChange={handleFileInputChange}
          onRemove={removePendingFile}
          isDragging={isDragging}
          disabled={createMutation.isPending}
        />

        {/* Only the tenant's REQUIRED ticket custom fields — the create endpoint
            refuses without them, so they are part of the minimum this dialog owes,
            not the optional detail the detail page collects. */}
        {requiredCfDefinitions.length > 0 && (
          <CustomFieldsSection
            entityType="support_ticket"
            requiredOnly
            values={customFields}
            onChange={setCustomFields}
            errors={customFieldErrors}
          />
        )}

        <p className="text-xs text-muted-foreground">
          {t('tickets.quickCreateHint')}
        </p>
      </div>
    </DialogForm>
  );
}
